<template>
  <thead>
    <tr>
      <th
        v-for="columnGroup of header"
        :key="columnGroup.key"
        :colspan="columnGroup.columns.reduce((prev, cur) => (cur.isActive ? prev + 1 : prev), 0)"
      >
        <div
          class="psui-flex psui-space-x-1 psui-items-center psui-show-childrens-on-hover"
          @mouseleave="emit('column-group-alert-status', columnGroup, $event, false)"
        >
          <p
            class="title"
            :class="{'is-warning-column': !!columnGroup.warning}"
          >
            {{ columnGroup.title }}
          </p>
          <span
            v-if="columnGroup.warning"
            class="column-group-warning-svg-position"
            style="position:  relative; margin-left: -12px; margin-top: -2px;"
            @mouseover="emit('column-group-alert-status', columnGroup, $event, true)"
          >
            <span class="cursor-pointer">
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <circle
                  cx="5"
                  cy="5"
                  r="4.5"
                  fill="#EDAB3E"
                />
              </svg>
            </span>
          </span>
          <PsIcon
            icon="info_outline"
            size="18"
            display="flex"
            class="helper psui-mt-[2px] psui-cursor-pointer psui-text-gray-60 hover:psui-text-blue-60 psui-opacity-0 psui-leading-none psui-transition"
            :aria-label="`${columnGroup.title} info`"
            aria-haspopup="true"
            :aria-controls="`modal-column-group-helper-${columnGroup.key}`"
            @click="emit('click-column-group-helper', columnGroup, $event)"
            @keydown.enter.stop="emit('click-column-group-helper', columnGroup, $event)"
          />
        </div>
      </th>
    </tr>

    <tr>
      <template v-for="(columnGroup, columnGroupIndex) of header">
        <th
          v-for="(column, index) of columnGroup.columns"
          :key="`${columnGroup.key}-${column.key}`"
          :data-test-id="column.key"
          class="pr-0"
          :class="{'pl-2':index == 0 && columnGroupIndex > 0}"
        >
          <div
            class="psui-flex psui-flex-col psui-justify-center py-[7px]"
            :class="columnGroupIndex == 0 ? 'psui-items-center' : 'psui-items-end'"
            :data-index="columnGroupIndex"
            @mouseleave="emit('column-alert-hide', column, $event, false)"
          >
            <div class="psui-show-childrens-on-hover absolute-childrens">
              <p
                v-tooltip="{
                  content: column.description,
                  placement: 'bottom',
                  classes: 'custom-tooltip'
                }"
                class="title"
                :class="{'is-warning-column': !!column.warning}"
                v-if="column.title"
              >
                {{ column.title }}
              </p>
              <span
                v-if="column.warning"
                class="warning-svg-position"
                @mouseover="emit('column-alert-show', column, $event, true)"
              >
                <span class="cursor-pointer">
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    fill="none"
                  >
                    <circle
                      cx="5"
                      cy="5"
                      r="4.5"
                      fill="#EDAB3E"
                    />
                  </svg>
                </span>
              </span>

              <PsIcon
                icon="info_outline"
                size="18"
                class="helper psui-cursor-pointer psui-text-gray-60 hover:psui-text-blue-60 psui-opacity-0 psui-leading-none psui-transition"
                display="flex"
                :aria-label="`${column.title} info`"
                aria-haspopup="true"
                :aria-controls="`modal-column-helper-${column.key}`"
                @click="emit('click-column-helper', column, $event)"
                @keydown.enter.stop="emit('click-column-helper', column, $event)"
              />

              <PsIcon
                v-if="showOrder"
                :icon="orderDirection == 'asc' ? 'arrow_downward' : 'arrow_upward'"
                :type="orderDirection == 'asc' ? 'arrow_upward' : 'arrow_upward'"
                size="18"
                class="helper psui-cursor-pointer psui-text-blue-50 psui-opacity-0 psui-leading-none psui-transition"
                :style="{ display: 'flex', marginTop: '1px' }"
                @click="emit('click-order-column', column)"
                @keydown.enter.stop="emit('click-order-column', column)"
              />
            </div>
          </div>
        </th>
      </template>
    </tr>
  </thead>
</template>

<script setup>
import PsIcon from '../ui/PsIcon.vue'

import { computed, getCurrentInstance } from 'vue'

defineProps({
  /**
   * It sets the title for the first column.
   */
  firstColumnTitle: {
    type: String,
    default: '',
  },
  /**
   * It sets the description for the first column.
   */
  firstColumnDescription: {
    type: String,
    default: '',
  },
  /**
   * It sets the values which will be use to render the header.
   */
  header: {
    type: Array,
    required: true,
    default: () => {
      return []
    },
  },
  /**
   * this sets whether sorting will be displayed.
   */
  showOrder: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click-column-group-helper', 'click-column-helper', 'click-order-column', 'column-alert-show', 'column-alert-hide'])

const parent = getCurrentInstance().proxy.$parent

const columnsSelectedForStudy = computed(() => {
  return parent.columnsSelectedForStudy
})

const orderColumn = computed(() => {
  return parent.orderColumn
})

const orderDirection = computed(() => {
  return parent.orderDirection
})
</script>

<style>
/* Please, use the file src/assets/scss/components/PsTableResults.scss */
</style>
